import React from 'react';

import { get } from '@lumapps/constants';
import { hasTermsAndConditions } from '@lumapps/customer/ducks/selectors';
import { isFeatureEnabled as isFeatureEnabledSelector } from '@lumapps/features';
import { doesCurrentInstanceAllowPublicContent } from '@lumapps/instance/ducks/selectors';
import { useSelector } from '@lumapps/redux/react';
import { useLocation } from '@lumapps/router';
import { hasUserAcceptedTermsAndConditions } from '@lumapps/user/ducks/selectors';
import { isInsideIframe } from '@lumapps/utils/browser/isInsideIframe';
import { DelayedSuspense } from '@lumapps/utils/components/DelayedSuspense';

import { removeBaseUrlFromPathname } from '../../utils/pathname';

const MOBILE_APP_MODAL_FEATURE_FLAG = 'mobile-deeplink';

/* istanbul ignore next */
const MobileAppModal = React.lazy(async () => {
    const resolved = await import(
        /* webpackChunkName: 'mobile-app-modal' */
        './MobileAppModal'
    );

    return { default: resolved.MobileAppModal };
});

const ConnectedMobileAppModal: React.FC<{ legacyPathname?: string }> = ({ legacyPathname }) => {
    const { mobileOS, isMobileAppWebView } = get();
    const { pathname: reactPathname } = useLocation();

    // if we are in the legacy, we must fix the pathname we get, else we simply use react router's pathname
    // we have to do this since pathname is not update by the useLocation hook when used inside the legacy
    const pathname = legacyPathname ? removeBaseUrlFromPathname(legacyPathname) : reactPathname;

    const isFeatureEnabled = useSelector(isFeatureEnabledSelector(MOBILE_APP_MODAL_FEATURE_FLAG));

    const isPublicContentAllowed = useSelector(doesCurrentInstanceAllowPublicContent);
    const isTermsAndConditionsFeatureEnabled = useSelector(hasTermsAndConditions);
    const areTermsAndConditionsAccepted = useSelector(hasUserAcceptedTermsAndConditions);

    const isTermsAndConditionsStepPassed =
        !isTermsAndConditionsFeatureEnabled ||
        isPublicContentAllowed ||
        (!isPublicContentAllowed && isTermsAndConditionsFeatureEnabled && areTermsAndConditionsAccepted);

    const isInIframe = React.useMemo(() => isInsideIframe(), []);

    return (
        <DelayedSuspense
            shouldLoad={
                isFeatureEnabled &&
                (mobileOS === 'Android' || mobileOS === 'iOS') &&
                !isMobileAppWebView &&
                isTermsAndConditionsStepPassed &&
                !isInIframe
            }
        >
            <MobileAppModal pathname={pathname} />
        </DelayedSuspense>
    );
};

export { MOBILE_APP_MODAL_FEATURE_FLAG, ConnectedMobileAppModal as MobileAppModal };
